/**
 * tesoreriaVista.js — los cuatro pasos de la tesorería del día, listos para pintar.
 *
 * POR QUÉ ESTE MÓDULO EXISTE
 * --------------------------
 * Los mismos números se muestran en DOS pantallas con vocabularios visuales
 * distintos: `YoolPayCierre` usa el Yool DS (`components/payments/yoolpay/cierre/
 * piezas.jsx`) y `YoolPayDia` usa `components/ui/*`. Ninguna de las dos debería
 * cambiar de idioma por esta feature, pero las dos tienen que mostrar EXACTAMENTE
 * las mismas cifras: son la misma plata. Así que la aritmética de presentación vive
 * aquí y los renderers sólo pintan. Si alguna vez difieren, es porque alguien empezó
 * a calcular en un componente.
 *
 * LA REGLA QUE NO SE PUEDE ROMPER: NINGÚN `?? 0` SOBRE UN DESCONOCIDO
 * ------------------------------------------------------------------
 * `saldo_psp_clp` viene en `null` y va a seguir viniendo en null por un buen rato:
 * el endpoint de payout resetea la conexión por falta de certificado mTLS. Un `?? 0`
 * en cualquier punto del camino haría que la pantalla diga «falta $0», y con eso el
 * manager gira sin reponer. Por eso el desconocido viaja como `null` y aparte va un
 * `saldo_disponible` que dice por qué. Mismo criterio para `cobertura_dias`,
 * `cargos_periodicos_clp` y la erosión.
 *
 * Es la misma razón por la que `formato.js` no usa `formatCLP`: un monto ausente no
 * es cero, y pintarlo como cero decide caja sobre una cifra falsa.
 *
 * TAMPOCO SE FILTRA POR MONTO
 * ---------------------------
 * `filasPorCompania` devuelve TODAS las compañías, incluidas las que tienen todo en
 * cero. Una compañía aparece con cifras en cero cuando su cobranza del día falló
 * entera — y ése es justamente el día que hay que ver. Filtrarla por «no tiene
 * plata» la borraría cuando más importa.
 *
 * @see src/services/yoolpay/diaApi.js — de dónde viene `data`
 * @see src/lib/yoolpay/formato.js — cómo se pintan los montos (fmtClp respeta null)
 */

/** ¿La respuesta del día trae tesorería? `null` = no se pudo leer la tarifa del PSP. */
export const hayTesoreria = (data) => !!(data && data.tesoreria);

/**
 * LA DECISIÓN — lo único de esta pantalla que instruye en vez de informar.
 *
 * `traspaso.monto_clp` SIEMPRE trae número, incluso sin saldo del PSP: ahí va la
 * comisión íntegra, que es el piso que garantiza cubrir el giro. Por eso aquí no hay
 * ningún `?? 0` de rescate — si llegara `null` sería un bug del backend, y taparlo
 * con un cero convertiría «no se pudo calcular» en «no hay que poner nada».
 *
 * `por_compania` sólo trae las que tienen plata que girar; el veredicto viene con su
 * motivo, y el motivo se muestra siempre: una instrucción sin razón no se obedece.
 */
export const decisionDelDia = (data) => {
  const d = data?.decision ?? null;
  if (!d) return null;
  return {
    traspaso: d.traspaso ?? null,
    por_compania: Array.isArray(d.por_compania) ? d.por_compania : [],
  };
};

/** Etiqueta y tono de cada veredicto. El tono NO es decorativo: ordena la acción. */
export const VEREDICTO = {
  girar: { label: 'Girar', tono: 'ok' },
  esperar: { label: 'Esperar', tono: 'baja' },
  confirmar: { label: 'Confirmar depósito', tono: 'media' },
  no_girar: { label: 'No girar', tono: 'alta' },
};

/**
 * Las acciones del día, con el depósito COLAPSADO en una sola.
 *
 * EL DEPÓSITO ES UN HECHO BANCARIO ÚNICO. El PSP deposita un monto por el lote, no uno por
 * aseguradora: el reparto entre compañías es cálculo interno nuestro y al banco no le
 * consta. La pantalla lo partía —«Confirmar depósito · YOOL DEMO 2 · $11.436» y «Confirmar
 * depósito · BCI · $8.169»— y eso manda a buscar en la cartola dos movimientos que no
 * existen. Hay UNO, por el neto del lote.
 *
 * LOS GIROS SÍ VAN POR COMPAÑÍA, y por eso no se agrupan: entra una transferencia y salen
 * N, una por aseguradora, cada una con su monto y su destino. La asimetría no es un
 * descuido del diseño — es de dónde viene y adónde va la plata.
 *
 * El veredicto por compañía que calcula payment sigue siendo correcto: responde «¿puedo
 * girarle a ésta?». Lo que estaba mal era convertir cada uno en una instrucción distinta.
 *
 * @param {object|null} decision lo que devuelve `decisionDelDia`
 * @returns {Array<object>} `{ id, tono, titulo, monto_clp, motivo, dias_vencido, companias }`
 */
export const accionesDelDia = (decision) => {
  const filas = Array.isArray(decision?.por_compania) ? decision.por_compania : [];
  const confirmar = filas.filter((c) => c.veredicto === 'confirmar');
  const resto = filas.filter((c) => c.veredicto !== 'confirmar');

  const acciones = resto.map((c) => {
    const v = VEREDICTO[c.veredicto] ?? { label: c.veredicto, tono: 'baja' };
    return {
      id: c.compania_id,
      tono: v.tono,
      titulo: `${v.label} · ${c.compania_nombre || c.compania_id}`,
      monto_clp: c.monto_a_girar_clp,
      motivo: c.motivo,
      dias_vencido: c.dias_vencido ?? 0,
      companias: [],
    };
  });

  if (confirmar.length > 0) {
    // El monto es la SUMA porque el depósito que se busca en el banco es uno solo: el neto
    // del lote. Las compañías van como contexto —a quién se le puede girar después— y no
    // como partes de un depósito que nadie va a encontrar por separado.
    const total = confirmar.reduce((s, c) => s + Number(c.monto_a_girar_clp ?? 0), 0);
    // El atraso que manda es el MAYOR: es el que dice hace cuánto se espera el depósito.
    const atraso = confirmar.reduce((m, c) => Math.max(m, c.dias_vencido ?? 0), 0);
    acciones.unshift({
      id: 'deposito-del-dia',
      tono: 'media',
      titulo: 'Confirmar el depósito del día',
      monto_clp: total,
      // Se toma el motivo de la primera: todas dicen lo mismo del mismo depósito, y
      // repetirlo una vez por compañía era justamente el ruido que se está quitando.
      motivo: confirmar[0].motivo,
      dias_vencido: atraso,
      companias: confirmar.map((c) => ({
        nombre: c.compania_nombre || c.compania_id,
        monto_clp: c.monto_a_girar_clp,
      })),
    });
  }

  return acciones;
};

/**
 * PASO 1 — El global del día: cuánto se cobró, cuánto cuesta el PSP, cuánto se gira.
 *
 * Devuelve `null` —y no un objeto de ceros— cuando no hay tesorería: unos ceros se
 * leerían como «hoy no entró plata», que es una afirmación distinta de «no se pudo
 * calcular».
 */
export const pasoGlobal = (tesoreria) => {
  if (!tesoreria || !tesoreria.total) return null;
  const t = tesoreria.total;
  return {
    cobros: t.cobros,
    debo_recaudar_clp: t.debo_recaudar_clp,
    // La comisión que el PSP informó. `null` si a algún cobro le falta el dato — no se
    // convierte a cero, que se leería como «el proveedor no retuvo nada».
    diferencia_psp_clp: t.diferencia_psp_clp ?? null,
    // EL NETO QUE EL PSP DECLARA DEPOSITAR. Es contra esta cifra que se compara la
    // cartola; sin ella «depositado por el PSP: —» no tiene referencia y un depósito
    // corto no se ve hasta cuadrar el mes.
    esperado_depositar_clp: t.esperado_depositar_clp ?? null,
    a_girar_clp: t.a_girar_clp,
    fecha_giro: tesoreria.fecha_giro ?? null,
    venta_nueva: tesoreria.venta_nueva ?? null,
    cobranza: tesoreria.cobranza ?? null,
    validacion: tesoreria.cobranza_validacion ?? null,
  };
};

/**
 * PASOS 2 y 3 — El desglose por compañía, con su venta nueva, su cobro y el reparto
 * del bruto que define lo que se le gira.
 *
 * `tiene_cobranza` decide si la fila muestra el bloque de validación: la venta nueva
 * no tiene esa dimensión —o se vendió o no existe—, así que mostrarlo vacío ahí
 * sugeriría que falta un dato en vez de que la pregunta no aplica.
 */
/** Qué dice cada problema de la metadata del PSP, en una línea accionable. */
const TEXTO_PROBLEMA = {
  sin_dato: 'El PSP no informó su comisión en este cobro',
  no_cierra: 'La aritmética del PSP no cierra: bruto − comisión ≠ lo que dice depositar',
  bruto_distinto: 'El monto que declara el PSP no es el que tenemos registrado',
  deposito_partido: 'El PSP deposita este cobro en varios tramos; se registró sólo el primero',
};

/**
 * La marca de una transacción: ✖ problema, ⚠ desvío, o nada.
 *
 * LOS DOS NIVELES NO SE MEZCLAN, y es lo que hace útil la marca:
 *
 *   ✖ `problema` — la metadata del PSP no cierra consigo misma, o falta. No depende de
 *     ninguna tarifa, así que no hay falsos positivos: hay que ir a ver ESE cobro.
 *   ⚠ `desvio` — la comisión efectiva se aparta de la tarifa configurada. **No es un
 *     error.** La tarifa real depende del medio de pago —un cobro de crédito presencial a
 *     1,89% + IVA se aparta del 2,89% online y está perfecto—, así que se muestra la tasa
 *     efectiva y decide una persona.
 *
 * Un ✖ tapa al ⚠: si el dato está roto, comparar tasas no aporta nada.
 */
export const marcasTransaccion = (tx) => {
  const problemas = Array.isArray(tx?.problemas) ? tx.problemas : [];
  if (problemas.length > 0) {
    return {
      nivel: 'problema',
      simbolo: '✖',
      texto: problemas.map((p) => TEXTO_PROBLEMA[p] ?? p).join(' · '),
      codigos: problemas,
    };
  }
  const d = tx?.desvio;
  if (d && typeof d.tasa_efectiva === 'number') {
    const pct = (v) => `${(v * 100).toFixed(4).replace('.', ',')}%`;
    const signo = (d.diferencia_clp ?? 0) > 0 ? 'más' : 'menos';
    return {
      nivel: 'desvio',
      simbolo: '⚠',
      texto: `Cobró ${pct(d.tasa_efectiva)} y la tarifa configurada dice `
        + `${pct(d.tasa_esperada)} — ${signo} de lo pactado. La tarifa depende del medio `
        + 'de pago, así que revisar antes de reclamar.',
      codigos: [],
    };
  }
  return null;
};

export const filasPorCompania = (tesoreria) => {
  const lista = tesoreria && Array.isArray(tesoreria.por_compania)
    ? tesoreria.por_compania
    : [];

  return lista.map((c) => {
    const validacion = c.cobranza_validacion ?? null;
    return {
      compania_id: c.compania_id,
      // El id y no un guion: una fila sin nombre igual tiene plata, y hay que poder
      // saber de quién es.
      nombre: c.compania_nombre || c.compania_id,
      cobros: c.total?.cobros ?? 0,
      debo_recaudar_clp: c.total?.debo_recaudar_clp ?? 0,
      // `?? null`, NO `?? 0`. Este cero era el fallback que el encabezado de este módulo
      // prohíbe: desde que la comisión la informa el PSP, `null` significa «a algún cobro
      // de esta compañía le falta el dato», y pintarlo como $0 diría que el proveedor no
      // retuvo nada. La comisión es lo que hay que reponer para poder girar.
      diferencia_psp_clp: c.total?.diferencia_psp_clp ?? null,
      esperado_depositar_clp: c.total?.esperado_depositar_clp ?? null,
      a_girar_clp: c.total?.a_girar_clp ?? 0,
      venta_nueva: c.venta_nueva ?? null,
      cobranza: c.cobranza ?? null,
      indeterminado: c.indeterminado ?? null,
      validacion,
      tiene_cobranza: (validacion?.esperadas ?? 0) > 0,
      transacciones: Array.isArray(c.transacciones) ? c.transacciones : [],
    };
  });
};

/**
 * PASO 4 — El traspaso a la cuenta de Yool, con sus DOS componentes y su tendencia.
 *
 *  (a) `diferencia_psp_clp` — la comisión de las transacciones del día.
 *  (b) `cargos_periodicos_clp` — mantención y demás cobros que no son por
 *      transacción. Hoy SIEMPRE `null`: no tienen fuente de datos. La pantalla debe
 *      decir «sin configurar», no `$0` — un cero afirmaría que el PSP no cobra
 *      mantención, que nadie verificó.
 *
 * Y la EROSIÓN, que es lo que el faltante del día no puede mostrar: con colchón el
 * faltante da cero y no informa nada. Se entrega aunque el saldo falte, porque no
 * depende de él.
 */
export const traspasoAYool = (tesoreria, erosion) => {
  // EL SALDO QUE MANDA ES EL DE LA CUENTA DESDE LA QUE SE GIRA: 110011 Settlement, en Odoo. El
  // giro real es manual desde el banco de Yool, así que el saldo del PSP no responde «¿puedo
  // girar?» — dice cuánto le queda por depositar. Y encima esa consulta falla (el adapter de
  // payout no presenta su certificado mTLS), así que este bloque mostraba cuatro casillas
  // vacías, incluida la que le da nombre.
  //
  // `typeof number` y no truthy en las dos: un saldo de CERO es un dato —la cuenta está vacía—
  // y tiene que distinguirse de «no se pudo leer».
  const settlement = tesoreria?.saldo_settlement_clp;
  const psp = tesoreria?.saldo_psp_clp;
  const saldo = typeof settlement === 'number' ? settlement
    : typeof psp === 'number' ? psp
      : null;
  const disponible = saldo !== null;

  return {
    diferencia_psp_clp: tesoreria?.total?.diferencia_psp_clp ?? null,
    a_girar_clp: tesoreria?.total?.a_girar_clp ?? null,
    cargos_periodicos_clp: erosion?.cargos_periodicos_clp ?? null,

    saldo_disponible: disponible,
    saldo_psp_clp: disponible ? saldo : null,
    /** De qué cuenta salió el saldo, para que la cifra diga su procedencia. */
    fuente_saldo: tesoreria?.fuente_saldo ?? null,
    /** Lo que hay en Collection para reponer: «traspasa $X» sin saber si hay de dónde no sirve. */
    saldo_collection_clp: typeof tesoreria?.saldo_collection_clp === 'number'
      ? tesoreria.saldo_collection_clp
      : null,
    /** Por qué falta —o no falta— plata. El bloque no lo decía. */
    motivo_traspaso: tesoreria?.motivo_traspaso ?? null,
    falta_poner_clp: typeof tesoreria?.falta_poner_clp === 'number'
      ? tesoreria.falta_poner_clp
      : null,

    // La tendencia. `null` cuando no hay sección de erosión: sin ella no se sabe
    // cuánto se consumió, y un cero diría que no se consumió nada.
    erosion_clp: erosion?.erosion_clp ?? null,
    ritmo_diario_clp: erosion?.ritmo_diario_clp ?? null,
    erosion_desde: erosion?.desde ?? null,
    erosion_hasta: erosion?.hasta ?? null,
    erosion_dias: erosion?.dias ?? null,
    cobros_sin_monto: erosion?.cobros_sin_monto ?? null,
    cobertura_dias: erosion?.cobertura_dias ?? null,
    fecha_sin_cobertura: erosion?.fecha_sin_cobertura ?? null,
  };
};
